import { AuthService, GsbEntityService, getGsbTenantCode, setGsbApiUrl, setGsbTenantCode, } from "@gsb-core/core";
import { googleCredentialInputSchema, googleCredentialPayloadSchema, resetPasswordConfirmInputSchema, resetPasswordInputSchema, tenantCodeSchema, } from "./contracts.js";
export class AuthMfaRequiredError extends Error {
    expiresAt;
    constructor(expiresAt) {
        super("A verification code has been sent to your email.");
        this.expiresAt = expiresAt;
        this.name = "AuthMfaRequiredError";
    }
}
export class AuthClientError extends Error {
    code;
    status;
    constructor(message, options = {}) {
        super(message);
        this.name = "AuthClientError";
        this.code = options.code;
        this.status = options.status;
    }
}
export function createCoreAuthClient(apiUrl, tenantCode, tenantApiUrl, recoveryOptions) {
    if (apiUrl)
        setGsbApiUrl(apiUrl);
    const configuredTenantCode = tenantCode
        ? tenantCodeSchema.parse(tenantCode)
        : undefined;
    if (configuredTenantCode)
        setGsbTenantCode(configuredTenantCode);
    function bindTenant(targetTenantCode, targetApiUrl) {
        setGsbTenantCode(targetTenantCode);
        setGsbApiUrl(targetApiUrl);
    }
    function bindConfiguredTenant() {
        if (configuredTenantCode)
            bindTenant(configuredTenantCode, apiUrl);
    }
    return {
        async verifySession(auth) {
            const sessionTenantCode = tenantCodeSchema.parse(auth.tenantCode ?? configuredTenantCode);
            try {
                const response = await new GsbEntityService(false, false, true, apiUrl).verifyToken({ variation: { tenantCode: sessionTenantCode } }, auth.token);
                return Boolean(response.auth?.userId);
            }
            catch {
                return false;
            }
        },
        adoptSession(auth) {
            const sessionTenantCode = tenantCodeSchema.parse(auth.tenantCode ?? configuredTenantCode);
            bindTenant(sessionTenantCode, apiUrl);
            const service = AuthService.getInstance();
            const tokenExpiry = service.getUserFromToken(auth.token)?.exp;
            const session = auth.expireDate || !tokenExpiry
                ? auth
                : {
                    ...auth,
                    expireDate: new Date(tokenExpiry * 1_000).toISOString(),
                };
            service.setToken(session.token, session, true);
            return Promise.resolve({
                user: toSessionUser(session),
                csrfToken: "direct-client",
            });
        },
        async login(input) {
            const loginTenantCode = tenantCodeSchema.parse(input.tenantCode);
            const loginApiUrl = apiUrl &&
                configuredTenantCode &&
                loginTenantCode !== configuredTenantCode &&
                tenantApiUrl
                ? tenantApiUrl(loginTenantCode)
                : apiUrl;
            bindTenant(loginTenantCode, loginApiUrl);
            const response = await AuthService.getInstance().login({
                email: input.email,
                password: input.password,
                mfaToken: input.mfaToken,
                remember: input.remember,
                variation: { tenantCode: loginTenantCode },
            });
            if (response.mfaRequired) {
                throw new AuthMfaRequiredError(response.mfaExpireDate);
            }
            if (!response.success || !response.userData)
                throw new AuthClientError(normalizeAuthenticationMessage(response.error), {
                    code: response.errorCode,
                    status: response.status,
                });
            return {
                user: toSessionUser(response.userData),
                csrfToken: "direct-client",
            };
        },
        async loginWithGoogle(credential) {
            bindConfiguredTenant();
            const parsedCredential = googleCredentialInputSchema.parse({
                credential,
            });
            const payload = parseGoogleCredentialPayload(parsedCredential.credential);
            const response = await AuthService.getInstance().login({
                email: payload.email,
                googleToken: parsedCredential.credential,
                variation: configuredTenantCode
                    ? { tenantCode: configuredTenantCode }
                    : undefined,
            });
            if (!response.success || !response.userData) {
                throw new AuthClientError(response.error ?? "Google authentication failed", {
                    ...(response.errorCode ? { code: response.errorCode } : {}),
                    ...(response.status !== undefined
                        ? { status: response.status }
                        : {}),
                });
            }
            return {
                user: toSessionUser(response.userData),
                csrfToken: "direct-client",
            };
        },
        sessionEndReason() {
            return AuthService.getInstance().getSessionEndReason();
        },
        session() {
            const service = AuthService.getInstance();
            if (!service.isAuthenticated())
                return Promise.resolve(null);
            const user = service.getCurrentUser();
            return Promise.resolve(user ? { user: toSessionUser(user), csrfToken: "direct-client" } : null);
        },
        logout() {
            AuthService.getInstance().logout();
            return Promise.resolve();
        },
        async requestPasswordReset(email) {
            bindConfiguredTenant();
            const response = await AuthService.getInstance().sendResetPasswordEmail(resetPasswordInputSchema.parse({ email }).email, recoveryOptions);
            assertSuccessfulPasswordResponse(response, "Could not send reset link");
            if (response.emailSent === false) {
                throw new Error("Could not send reset link");
            }
        },
        async resetPassword(input) {
            bindConfiguredTenant();
            const parsed = resetPasswordConfirmInputSchema.parse(input);
            const response = await AuthService.getInstance().changePassword("", parsed.newPassword, parsed.email, parsed.token);
            assertSuccessfulPasswordResponse(response, "Password reset failed");
        },
    };
}
export function createCorePublicAuthClient(apiUrl, tenantCode, recoveryOptions) {
    const configuredTenantCode = tenantCode
        ? tenantCodeSchema.parse(tenantCode)
        : undefined;
    const entityService = new GsbEntityService(false, false, true, apiUrl);
    function requireTenantCode() {
        if (!configuredTenantCode) {
            throw new Error("A tenant code is required for password recovery");
        }
        return configuredTenantCode;
    }
    return {
        async requestPasswordReset(email) {
            const tenant = requireTenantCode();
            const parsed = resetPasswordInputSchema.parse({ email });
            const response = await entityService.runWfFunction({
                function: { name: "sendResetPasswordEmail" },
                instance: {
                    prms: { email: parsed.email, ...recoveryOptions },
                    entity: { email: parsed.email },
                },
            }, undefined, tenant);
            assertSuccessfulPasswordResponse(response, "Could not send reset link");
            if (response.emailSent === false)
                throw new Error("Could not send reset link");
        },
        async resetPassword(input) {
            const tenant = requireTenantCode();
            const parsed = resetPasswordConfirmInputSchema.parse(input);
            const response = await entityService.changePassword({
                oldPassword: "",
                newPassword: parsed.newPassword,
                email: parsed.email,
                resetToken: parsed.token,
            }, undefined, tenant);
            assertSuccessfulPasswordResponse(response, "Password reset failed");
        },
    };
}
function parseGoogleCredentialPayload(credential) {
    const payload = credential.split(".")[1];
    if (!payload)
        throw new Error("Invalid Google credential");
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const bytes = Uint8Array.from(globalThis.atob(padded), (character) => character.charCodeAt(0));
    return googleCredentialPayloadSchema.parse(JSON.parse(new TextDecoder().decode(bytes)));
}
function assertSuccessfulPasswordResponse(response, fallback) {
    if (!response || typeof response !== "object") {
        throw new Error(fallback);
    }
    const status = "status" in response ? response.status : undefined;
    const success = "success" in response ? response.success : undefined;
    if (success === false) {
        const message = "message" in response ? response.message : undefined;
        throw new Error(typeof message === "string" && message ? message : fallback);
    }
    if (typeof status === "number" && status >= 400) {
        const message = "message" in response ? response.message : undefined;
        throw new Error(typeof message === "string" && message ? message : fallback);
    }
}
function toSessionUser(user) {
    return {
        id: user.userId ?? user.email ?? "gsb-user",
        email: user.email,
        displayName: [user.name, user.surname].filter(Boolean).join(" ") || undefined,
        tenantCode: getGsbTenantCode(),
        permissions: user.roles ?? [],
    };
}
function normalizeAuthenticationMessage(message) {
    return (message ?? "Authentication failed").replace(/\bcridentials\b/gi, "credentials");
}
//# sourceMappingURL=client.js.map